import React, { useState } from 'react';
import { toast } from '../utils/toast';
import { geminiService } from '../services/geminiService';
import { QuickStart } from '../components/QuickStart';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { BetaBadge, BetaMessage } from '../components/BetaDisclaimer';
import DeploymentOptions from '../components/DeploymentOptions';

export function TestScriptGenerator() {
  const [contentInput, setContentInput] = useState('');
  const [testType, setTestType] = useState('unit');
  const [framework, setFramework] = useState('jest');
  const [testLevel, setTestLevel] = useState('basic');
  const [language, setLanguage] = useState('javascript');
  const [generatedContent, setGeneratedContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [includeComments, setIncludeComments] = useState(true);
  const [includeMocks, setIncludeMocks] = useState(true);
  const [includeSetup, setIncludeSetup] = useState(true);

  const testTypeOptions = [
    { value: 'unit', label: 'Unit Tests', description: 'Test individual components or functions' },
    { value: 'integration', label: 'Integration Tests', description: 'Test component interactions' },
    { value: 'e2e', label: 'End-to-End Tests', description: 'Full application flow tests' },
    { value: 'api', label: 'API Tests', description: 'Test API endpoints and responses' },
    { value: 'performance', label: 'Performance Tests', description: 'Load and stress testing' },
    { value: 'security', label: 'Security Tests', description: 'Security and vulnerability testing' }
  ];

  const frameworkOptions = [
    { value: 'jest', label: 'Jest', description: 'Popular JavaScript testing framework' },
    { value: 'mocha', label: 'Mocha', description: 'Flexible JavaScript test framework' },
    { value: 'pytest', label: 'PyTest', description: 'Python testing framework' },
    { value: 'junit', label: 'JUnit', description: 'Java testing framework' },
    { value: 'rspec', label: 'RSpec', description: 'Ruby testing framework' },
    { value: 'phpunit', label: 'PHPUnit', description: 'PHP testing framework' }
  ];

  const testLevelOptions = [
    { value: 'basic', label: 'Basic Coverage' },
    { value: 'comprehensive', label: 'Comprehensive' },
    { value: 'edge', label: 'Edge Cases' },
    { value: 'advanced', label: 'Advanced Scenarios' }
  ];

  const languageOptions = [
    { value: 'javascript', label: 'JavaScript/TypeScript' },
    { value: 'python', label: 'Python' },
    { value: 'java', label: 'Java' },
    { value: 'ruby', label: 'Ruby' },
    { value: 'php', label: 'PHP' },
    { value: 'csharp', label: 'C#' }
  ];

  const quickStartGuides = [
    {
      title: "Unit Testing Best Practices",
      description: "Learn how to write effective unit tests for your code.",
      keywords: ["Assertions", "Mocking", "Test Structure"],
      example: "Example: Testing a user authentication service with mocked dependencies",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: "Integration Testing Guide",
      description: "Create robust integration tests for your application.",
      keywords: ["API Testing", "Database", "Services"],
      example: "Example: Testing the interaction between a payment service and order system",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      title: "E2E Testing Strategies",
      description: "End-to-end testing approaches and best practices.",
      keywords: ["User Flows", "Automation", "Scenarios"],
      example: "Example: Testing a complete user registration and checkout process",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      )
    }
  ];

  const handleTranscriptionComplete = (transcribedText) => {
    setContentInput(transcribedText);
  };

  const generateContent = async () => {
    if (!contentInput.trim()) {
      toast.error('Please provide test requirements');
      return;
    }

    setIsGenerating(true);
    try {
      const prompt = `Generate a ${testType} test script for: ${contentInput}
Language: ${language}
Framework: ${framework}
Test Level: ${testLevel}
Include Comments: ${includeComments}
Include Mocks: ${includeMocks}
Include Setup: ${includeSetup}

Please provide:
- Complete test script with proper imports and setup
- Test cases with clear descriptions
- ${includeMocks ? 'Mock implementations and test data' : 'Test data only'}
- ${includeComments ? 'Detailed comments explaining the test strategy' : 'Minimal necessary comments'}
- Best practices and considerations
- Error handling scenarios

Format the response with clear sections and proper code structure for ${language} using ${framework}.`;

      const response = await geminiService.generateCode(prompt, {
        language,
        framework
      });

      if (response.error) {
        throw new Error(response.error);
      } else {
        const generatedText = response.content || response.message;
        if (generatedText) {
          setGeneratedContent(generatedText);
          toast.success('Test script generated successfully!');
        } else {
          throw new Error('No content received from the API');
        }
      }
    } catch (error) {
      console.error('Error generating test script:', error);
      toast.error(error.message || 'Failed to generate test script');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Modern SaaS Header */}
      <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-purple-900 via-purple-800 to-purple-900 shadow-2xl overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-purple-400/20 to-transparent"></div>
          <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-purple-400/20 to-transparent"></div>
          
          <div className="absolute -left-48 -top-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-purple-500/40 via-purple-300/10 to-purple-400/40 blur-xl opacity-30"></div>
          </div>
          <div className="absolute -right-48 -bottom-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-purple-400/40 via-purple-300/10 to-purple-500/40 blur-xl opacity-30"></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-purple-800/50 border border-purple-700/50 backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse"></div>
                <span className="text-sm text-purple-100">AI-Powered Testing</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white flex items-center">
                Test Script Generator
                <BetaBadge className="ml-3" />
              </h1>
              <p className="text-lg text-purple-200 max-w-xl">
                Generate comprehensive test scripts for your software
              </p>
              <BetaMessage />
              <div className="flex items-center gap-4 pt-2">
                <div className="flex items-center space-x-2 text-sm text-purple-200">
                  <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Multiple Frameworks</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-purple-200">
                  <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Best Practices</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .bg-gradient-conic {
          background-image: conic-gradient(var(--tw-gradient-stops));
        }
      `}</style>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        <div className="p-6">
          {/* Target Audience Banner */}
          <div className="mb-8 p-4 bg-purple-50 rounded-lg">
            <h3 className="text-lg font-medium text-purple-900 mb-2">Who is this tool for?</h3>
            <ul className="list-disc list-inside text-purple-700 space-y-1">
              <li>Software Developers - Generate unit and integration tests</li>
              <li>QA Engineers - Create comprehensive test suites</li>
              <li>Test Automation Engineers - Build automated test scripts</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Left column - Content Generation */}
            <div className="space-y-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Test Settings</h2>
                  <div className="space-y-6">
                    {/* Voice Recorder */}
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Record Test Requirements
                      </label>
                      <VoiceRecorder 
                        onTranscriptionComplete={handleTranscriptionComplete}
                        placeholder="Click to start recording test requirements..."
                      />
                    </div>

                    {/* Content Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Test Requirements
                      </label>
                      <textarea
                        value={contentInput}
                        onChange={(e) => setContentInput(e.target.value)}
                        rows="4"
                        placeholder="Describe what you want to test, including the component, function, or feature..."
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>

                    {/* Language and Framework */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Programming Language
                        </label>
                        <div className="grid grid-cols-1 gap-2">
                          {languageOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setLanguage(option.value)}
                              className={`flex items-center justify-center px-4 py-2 rounded-lg border ${
                                language === option.value
                                  ? 'bg-purple-50 border-purple-500 text-purple-700 shadow-sm'
                                  : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Testing Framework
                        </label>
                        <div className="grid grid-cols-1 gap-2">
                          {frameworkOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setFramework(option.value)}
                              className={`flex items-center justify-center px-4 py-2 rounded-lg border ${
                                framework === option.value
                                  ? 'bg-purple-50 border-purple-500 text-purple-700 shadow-sm'
                                  : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Test Type and Level */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Test Type
                        </label>
                        <div className="grid grid-cols-1 gap-2">
                          {testTypeOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setTestType(option.value)}
                              className={`flex items-center justify-center px-4 py-2 rounded-lg border ${
                                testType === option.value
                                  ? 'bg-purple-50 border-purple-500 text-purple-700 shadow-sm'
                                  : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Test Level
                        </label>
                        <div className="grid grid-cols-1 gap-2">
                          {testLevelOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setTestLevel(option.value)}
                              className={`flex items-center justify-center px-4 py-2 rounded-lg border ${
                                testLevel === option.value
                                  ? 'bg-purple-50 border-purple-500 text-purple-700 shadow-sm'
                                  : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Additional Options */}
                    <div className="space-y-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Additional Options
                      </label>
                      <div className="flex flex-wrap gap-4">
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            checked={includeComments}
                            onChange={(e) => setIncludeComments(e.target.checked)}
                            className="form-checkbox h-5 w-5 text-purple-600"
                          />
                          <span className="ml-2 text-gray-700">Include Detailed Comments</span>
                        </label>
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            checked={includeMocks}
                            onChange={(e) => setIncludeMocks(e.target.checked)}
                            className="form-checkbox h-5 w-5 text-purple-600"
                          />
                          <span className="ml-2 text-gray-700">Include Mocks</span>
                        </label>
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            checked={includeSetup}
                            onChange={(e) => setIncludeSetup(e.target.checked)}
                            className="form-checkbox h-5 w-5 text-purple-600"
                          />
                          <span className="ml-2 text-gray-700">Include Setup/Teardown</span>
                        </label>
                      </div>
                    </div>

                    <button
                      onClick={generateContent}
                      disabled={isGenerating}
                      className={`w-full px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                        isGenerating
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-purple-600 hover:bg-purple-700'
                      }`}
                    >
                      {isGenerating ? (
                        <div className="flex items-center justify-center">
                          <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Generating Test Script...
                        </div>
                      ) : (
                        'Generate Test Script'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column - Generated Content */}
            <div className="space-y-8">
              {generatedContent ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-semibold text-gray-900">Generated Test Script</h3>
                      <button
                        onClick={() => copyToClipboard(generatedContent)}
                        className="inline-flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy to Clipboard
                      </button>
                    </div>
                    <div className="prose max-w-none">
                      <div className="whitespace-pre-wrap bg-gray-50 rounded-lg p-6 border border-gray-200">
                        <pre>
                          <code className={`language-${language}`}>
                            {generatedContent}
                          </code>
                        </pre>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <div className="text-center py-12">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No test script generated yet</h3>
                      <p className="mt-1 text-sm text-gray-500">Fill in the test requirements and click generate to create your test script.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <QuickStart guides={quickStartGuides} />
      <DeploymentOptions appId="test-script-generator" position="none" />
    </div>
  );
}

export default TestScriptGenerator; 